import { useContext, useMemo, useRef, type RefObject } from "react";
import { IdentityContext, privateKey, type IdentityGeneration } from "../lib/identity-scope";

/**
 * The account the client's private state belongs to now (SEC-2, T-910) — the
 * generation `SessionProvider` publishes. The one reader of `IdentityContext`.
 */
export function useIdentity(): IdentityGeneration {
  return useContext(IdentityContext);
}

/**
 * A private query key for the account signed in now: `[generation, ...key]`
 * (SEC-2, T-910 — `lib/identity-scope.ts` has the why and the private/public
 * rule). One source: {@link useIdentity}.
 *
 * Use it for every query whose response depends on the session cookie, and
 * pass the SAME returned key to `setQueryData`/`getQueryState` in that hook's
 * mutations, so a write always lands in the entry the read reads.
 */
export function usePrivateKey<const K extends readonly unknown[]>(
  key: K,
): readonly [IdentityGeneration, ...K] {
  const identity = useIdentity();
  // Memoised so a callback that closes over the key keeps its identity across
  // renders; the tail is a module constant at every call site.
  return useMemo(() => privateKey(identity, key), [identity, key]);
}

/**
 * The generation as of the latest render, for async continuations: capture
 * `ref.current` when the work starts, and drop the result if it has moved by
 * the time the work resolves — a response that belongs to the previous account
 * must not write state the next one reads (T-910 arm 4).
 */
export function useIdentityRef(): RefObject<IdentityGeneration> {
  const identity = useIdentity();
  const ref = useRef(identity);
  ref.current = identity;
  return ref;
}
